import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../contexts/ToastContext';
import { obtenerEstadisticasKyc, type EstadisticasKyc } from '../../../services/kycService';
import { mensajeError } from '../correo/errores';
import { DECISIONES, DECISION_ETIQUETA, DECISION_COLOR, etiquetaPlataforma, hoyPanama, haceDiasPanama } from './formato';
import styles from './KycModule.module.css';

const nf = new Intl.NumberFormat('es-PA');
const pct = (parte: number, total: number) => (total > 0 ? Math.round((parte / total) * 1000) / 10 : 0);
const PRESETS = [7, 30, 90] as const;
const COLORES_PLATAFORMA: Record<string, string> = { ios: '#3b82f6', android: '#1f8a5b' };

function TooltipKyc({ active, payload, label }:
  { active?: boolean; payload?: Array<{ name?: string; value?: number; color?: string }>; label?: string }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + Number(p.value ?? 0), 0);
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipTitulo}>{label} · {nf.format(total)}</div>
      {payload.filter((p) => Number(p.value) > 0).map((p, i) => (
        <div key={i} className={styles.tooltipFila}>
          <span className={styles.tooltipPunto} style={{ background: p.color }} />
          <span className={styles.tooltipNombre}>{p.name}</span>
          <b className={styles.tooltipValor}>{nf.format(Number(p.value ?? 0))}</b>
        </div>
      ))}
    </div>
  );
}

/** Lista con barra proporcional: legible aunque una categoría domine (no es un pastel). */
function Distribucion({ titulo, datos, etiqueta, colores }:
  { titulo: string; datos: Record<string, number>; etiqueta?: (k: string) => string; colores?: Record<string, string> }) {
  const filas = Object.entries(datos).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const total = filas.reduce((s, [, n]) => s + n, 0);
  return (
    <div className={styles.grafica}>
      <div className={styles.graficaTitulo}>{titulo}</div>
      {total === 0 ? (
        <p className={styles.graficaVacia}>Sin validaciones en el período.</p>
      ) : (
        <ul className={styles.tipoLista}>
          {filas.map(([k, n]) => {
            const color = colores?.[k] ?? '#64748b';
            return (
              <li key={k} className={styles.tipoFila}>
                <span className={styles.tipoPunto} style={{ background: color }} />
                <span className={styles.tipoNombre}>{etiqueta ? etiqueta(k) : k === 'desconocido' ? 'Sin dato' : k.toUpperCase()}</span>
                <span className={styles.tipoBarra}><span style={{ width: `${pct(n, total)}%`, background: color }} /></span>
                <b className={styles.tipoValor}>{nf.format(n)}</b>
                <span className={styles.tipoPct}>{pct(n, total)}%</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function KycDashboardPage() {
  const { addToast } = useToast();
  const [desde, setDesde] = useState(haceDiasPanama(29));
  const [hasta, setHasta] = useState(hoyPanama());
  const [datos, setDatos] = useState<EstadisticasKyc | null>(null);
  const [cargando, setCargando] = useState(true);

  const reqId = useRef(0);
  const cargar = useCallback(async (d = desde, h = hasta) => {
    const id = ++reqId.current;
    setCargando(true);
    try {
      const r = await obtenerEstadisticasKyc({ desde: d, hasta: h });
      if (id === reqId.current) setDatos(r);
    } catch (err) {
      if (id === reqId.current) addToast('error', mensajeError(err));
    } finally {
      if (id === reqId.current) setCargando(false);
    }
  }, [desde, hasta, addToast]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, []);

  const aplicarPreset = (dias: number) => {
    const d = haceDiasPanama(dias - 1);
    const h = hoyPanama();
    setDesde(d); setHasta(h);
    cargar(d, h);
  };

  const kpis = useMemo(() => {
    if (!datos) return [];
    const t = datos.total;
    const n = (k: string) => t.porDecision[k] ?? 0;
    const prom = (s: number, c: number) => (c > 0 ? `${Math.round((s / c) * 10) / 10}%` : '—');
    return [
      { etiqueta: 'Validaciones', valor: nf.format(t.total), tono: '#3b82f6' },
      { etiqueta: 'Aprobadas', valor: nf.format(n('APPROVED')), pie: `${pct(n('APPROVED'), t.total)}% de aprobación`, tono: DECISION_COLOR.APPROVED },
      { etiqueta: 'Rechazadas', valor: nf.format(n('REJECTED')), pie: `${pct(n('REJECTED'), t.total)}%`, tono: DECISION_COLOR.REJECTED },
      { etiqueta: 'Revisión manual', valor: nf.format(n('MANUAL_REVIEW')), pie: `${pct(n('MANUAL_REVIEW'), t.total)}%`, tono: DECISION_COLOR.MANUAL_REVIEW },
      { etiqueta: 'Liveness promedio', valor: prom(t.sumaLiveness, t.nLiveness), tono: '#8b5cf6' },
      { etiqueta: 'Coincidencia facial promedio', valor: prom(t.sumaFaceMatch, t.nFaceMatch), tono: '#14b8a6' },
    ];
  }, [datos]);

  const serie = useMemo(() => (datos?.serie ?? []).map((d) => ({
    fecha: d.fecha.slice(5).split('-').reverse().join('/'),
    ...Object.fromEntries(DECISIONES.map((k) => [k, d.porDecision[k] ?? 0])),
  })), [datos]);

  return (
    <div>
      <div className={styles.toolbar}>
        <div className={styles.campo}>
          <label htmlFor="kyc-desde">Desde</label>
          <input id="kyc-desde" type="date" value={desde} max={hasta} onChange={(e) => setDesde(e.target.value)} />
        </div>
        <div className={styles.campo}>
          <label htmlFor="kyc-hasta">Hasta</label>
          <input id="kyc-hasta" type="date" value={hasta} min={desde} max={hoyPanama()} onChange={(e) => setHasta(e.target.value)} />
        </div>
        <Button variant="secondary" size="sm" onClick={() => cargar()} disabled={cargando}>
          <RefreshCw size={15} /> Actualizar
        </Button>
        <div className={styles.acciones}>
          {PRESETS.map((d) => (
            <Button key={d} variant="ghost" size="sm" onClick={() => aplicarPreset(d)} disabled={cargando}>
              Últimos {d} días
            </Button>
          ))}
        </div>
      </div>

      {cargando && !datos ? (
        <div className={styles.vacio}><Loader2 className="spinner" size={28} /></div>
      ) : !datos ? (
        <div className={styles.vacio}>No se pudieron cargar las estadísticas.</div>
      ) : (
        <>
          <div className={styles.kpis}>
            {kpis.map((k) => (
              <div key={k.etiqueta} className={styles.kpi} style={{ ['--kpi-tono' as string]: k.tono }}>
                <div className={styles.kpiValor}>{k.valor}</div>
                <div className={styles.kpiEtiqueta}>{k.etiqueta}</div>
                {k.pie && <div className={styles.kpiPie}>{k.pie}</div>}
              </div>
            ))}
          </div>

          <div className={styles.graficas}>
            <div className={`${styles.grafica} ${styles.graficaAncha}`}>
              <div className={styles.graficaTitulo}>Validaciones por día</div>
              {datos.total.total === 0 ? (
                <p className={styles.graficaVacia}>Sin validaciones en el período.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={serie} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                    <CartesianGrid stroke="var(--color-border-soft)" vertical={false} />
                    <XAxis dataKey="fecha" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis fontSize={11} allowDecimals={false} tickLine={false} axisLine={false} width={44} />
                    <Tooltip content={<TooltipKyc />} cursor={{ fill: 'var(--color-border-soft)' }} />
                    {DECISIONES.map((k) => (
                      <Bar key={k} dataKey={k} name={DECISION_ETIQUETA[k]} stackId="d" fill={DECISION_COLOR[k]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
              <div className={styles.leyenda}>
                {DECISIONES.map((k) => (
                  <span key={k} className={styles.leyendaItem}>
                    <span className={styles.leyendaPunto} style={{ background: DECISION_COLOR[k] }} />{DECISION_ETIQUETA[k]}
                  </span>
                ))}
              </div>
            </div>

            <Distribucion titulo="Por plataforma" datos={datos.total.porPlataforma} etiqueta={etiquetaPlataforma} colores={COLORES_PLATAFORMA} />
            <Distribucion titulo="Por resultado" datos={datos.total.porDecision} etiqueta={(k) => DECISION_ETIQUETA[k] ?? k} colores={DECISION_COLOR} />
            <Distribucion titulo="Por tipo de documento" datos={datos.total.porDocumento} />
            <Distribucion titulo="Por país del documento" datos={datos.total.porPais} />
          </div>

          <p className={styles.aviso}>
            Datos de este ambiente · actualizado {new Date(datos.actualizadoEn).toLocaleTimeString('es-PA', { hour: '2-digit', minute: '2-digit' })}.
            {' '}El día de hoy se recalcula cada 5 minutos; los días anteriores se consolidan cada noche.
            {datos.diasSinCalcular > 0 && (
              <> <b>{datos.diasSinCalcular} día(s) del rango aún no se consolidaron</b> y no se cuentan todavía.</>
            )}
          </p>
        </>
      )}
    </div>
  );
}
